<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.ADMIN.5 — full-screen "Turn on two-step verification"
// for administrators without a method (canvas: AdminRequired). During the
// grace period it can be put off for this browser session; once the grace
// period is over it cannot (the account API answers 403 anyway).
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Auth from 'dashboard/api/auth';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KaptionTwoStepSetup from 'dashboard/components/kaption/KaptionTwoStep/KaptionTwoStepSetup.vue';
import { useKaptionTwoStep } from 'dashboard/composables/useKaptionTwoStep';
import {
  getBrandTheme,
  resolveLoginBrandTheme,
} from 'dashboard/helper/kaptionBrandThemes';

const SNOOZE_KEY = 'kaption_two_step_admin_snoozed';

const { t } = useI18n();
const brandTheme = getBrandTheme(resolveLoginBrandTheme(null));
const {
  status,
  isEnabled,
  needsSetup,
  isBlocked,
  daysLeft,
  requiredAccountName,
  passkeyAvailable,
  fetchStatus,
  setStatus,
} = useKaptionTwoStep();

const setupRef = ref(null);
const snoozed = ref(false);
const wasBlocked = ref(false);

const readSnooze = () => {
  try {
    snoozed.value = window.sessionStorage.getItem(SNOOZE_KEY) === '1';
  } catch (e) {
    snoozed.value = false;
  }
};

onMounted(() => {
  readSnooze();
  if (isEnabled.value) fetchStatus();
});
watch(isEnabled, enabled => enabled && fetchStatus());

const visible = computed(
  () => needsSetup.value && (isBlocked.value || !snoozed.value)
);

const authenticatorAvailable = computed(
  () => !!status.value?.methods?.authenticator?.available
);

const description = computed(() =>
  isBlocked.value
    ? t('KAPTION_TWO_STEP.ADMIN_REQUIRED.BLOCKED_DESC', {
        account: requiredAccountName.value,
      })
    : t('KAPTION_TWO_STEP.ADMIN_REQUIRED.DESC', {
        account: requiredAccountName.value,
      })
);

const laterLabel = computed(() => {
  if (daysLeft.value === null || daysLeft.value <= 0)
    return t('KAPTION_TWO_STEP.ADMIN_REQUIRED.LATER_TODAY');
  if (daysLeft.value === 1)
    return t('KAPTION_TWO_STEP.ADMIN_REQUIRED.LATER_ONE');
  return t('KAPTION_TWO_STEP.ADMIN_REQUIRED.LATER', { days: daysLeft.value });
});

const snooze = () => {
  try {
    window.sessionStorage.setItem(SNOOZE_KEY, '1');
  } catch (e) {
    // private mode: the screen just closes for now
  }
  snoozed.value = true;
};

const signOut = () => Auth.logout();

const onUpdated = data => {
  if (isBlocked.value) wasBlocked.value = true;
  setStatus(data);
};

// The account API answered 403 while blocked: once the setup flow is over
// (backup codes saved), reload so every screen fetches its data again.
const onFinished = () => {
  if (wasBlocked.value && status.value?.has_factor) window.location.reload();
};
</script>

<template>
  <div
    v-if="visible"
    class="fixed inset-0 z-[60] flex flex-col items-center overflow-y-auto py-20 px-4 bg-n-brand/5 dark:bg-n-background backdrop-blur-sm"
    data-testid="kpt-two-step-admin-required"
  >
    <div class="fixed inset-0 -z-10 bg-n-background" />
    <img
      :src="brandTheme.login.light"
      :alt="brandTheme.name"
      class="mx-auto dark:hidden"
      :class="brandTheme.login.sizeClass"
    />
    <img
      :src="brandTheme.login.dark"
      :alt="brandTheme.name"
      class="hidden mx-auto dark:block"
      :class="brandTheme.login.sizeClass"
    />
    <section
      class="flex flex-col w-full max-w-lg gap-6 bg-white shadow mt-11 dark:bg-n-solid-2 p-11 sm:shadow-lg sm:rounded-lg"
    >
      <div class="flex flex-col items-center text-center">
        <span
          class="flex items-center justify-center mb-4 rounded-full size-14 outline outline-1 outline-n-weak"
        >
          <Icon icon="i-lucide-shield" class="size-6 text-n-slate-10" />
        </span>
        <h2 class="text-2xl font-semibold text-n-slate-12">
          {{ $t('KAPTION_TWO_STEP.ADMIN_REQUIRED.TITLE') }}
        </h2>
        <p class="mt-2 text-sm leading-relaxed text-n-slate-11">
          {{ description }}
        </p>
      </div>
      <div
        class="flex flex-col overflow-hidden border rounded-lg border-n-weak [&>*+*]:border-t [&>*+*]:border-n-weak"
      >
        <button
          v-if="passkeyAvailable"
          type="button"
          class="flex items-center gap-3 px-4 py-3.5 text-left hover:bg-n-alpha-1"
          data-testid="kpt-two-step-admin-passkey"
          @click="setupRef?.add('passkey')"
        >
          <Icon icon="i-lucide-key-round" class="size-[18px] text-n-slate-10" />
          <span class="flex flex-col flex-1 gap-0.5">
            <span class="flex items-center gap-2">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ $t('KAPTION_TWO_STEP.METHODS.PASSKEY.TITLE') }}
              </span>
              <span
                class="px-1.5 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
              >
                {{ $t('KAPTION_TWO_STEP.BADGE.RECOMMENDED') }}
              </span>
            </span>
            <span class="text-[13px] text-n-slate-11">
              {{ $t('KAPTION_TWO_STEP.METHODS.PASSKEY.LOGIN_DESC') }}
            </span>
          </span>
          <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-10" />
        </button>
        <button
          type="button"
          class="flex items-center gap-3 px-4 py-3.5 text-left hover:bg-n-alpha-1"
          :class="{ 'border-t border-n-weak': passkeyAvailable }"
          data-testid="kpt-two-step-admin-whatsapp"
          @click="setupRef?.add('whatsapp')"
        >
          <Icon
            icon="i-lucide-message-circle"
            class="size-[18px] text-n-slate-10"
          />
          <span class="flex flex-col flex-1 gap-0.5">
            <span class="text-sm font-semibold text-n-slate-12">
              {{ $t('KAPTION_TWO_STEP.METHODS.WHATSAPP.TITLE') }}
            </span>
            <span class="text-[13px] text-n-slate-11">
              {{ $t('KAPTION_TWO_STEP.METHODS.WHATSAPP.ADMIN_DESC') }}
            </span>
          </span>
          <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-10" />
        </button>
        <button
          v-if="authenticatorAvailable"
          type="button"
          class="flex items-center gap-3 px-4 py-3.5 text-left border-t border-n-weak hover:bg-n-alpha-1"
          data-testid="kpt-two-step-admin-authenticator"
          @click="setupRef?.add('authenticator')"
        >
          <Icon
            icon="i-lucide-smartphone"
            class="size-[18px] text-n-slate-10"
          />
          <span class="flex flex-col flex-1 gap-0.5">
            <span class="text-sm font-semibold text-n-slate-12">
              {{ $t('KAPTION_TWO_STEP.METHODS.AUTHENTICATOR.TITLE') }}
            </span>
            <span class="text-[13px] text-n-slate-11">
              {{ $t('KAPTION_TWO_STEP.METHODS.AUTHENTICATOR.ADMIN_DESC') }}
            </span>
          </span>
          <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-10" />
        </button>
      </div>
      <div class="flex flex-col items-center gap-2.5 text-sm">
        <button
          v-if="!isBlocked"
          type="button"
          class="font-medium text-n-brand hover:underline"
          data-testid="kpt-two-step-admin-later"
          @click="snooze"
        >
          {{ laterLabel }}
        </button>
        <button
          type="button"
          class="text-n-slate-11 hover:text-n-slate-12"
          @click="signOut"
        >
          {{ $t('KAPTION_TWO_STEP.ADMIN_REQUIRED.SIGN_OUT') }}
        </button>
      </div>
    </section>
  </div>
  <KaptionTwoStepSetup
    v-if="isEnabled"
    ref="setupRef"
    @updated="onUpdated"
    @finished="onFinished"
  />
</template>
